/**
 * Utility functions for anonymizing sensitive data in client lists
 */

/**
 * Anonymizes an email address by masking the middle part
 * Example: john.doe@example.com -> jo***.doe@ex***.com
 */
export const anonymizeEmail = (email: string): string => {
  if (!email || typeof email !== 'string') return email

  const [localPart, domain] = email.split('@')
  if (!localPart || !domain) return email

  // Anonymize local part (before @)
  const anonymizedLocal = anonymizeMiddlePart(localPart)

  // Anonymize domain part
  const [domainName, extension] = domain.split('.')
  const anonymizedDomain = anonymizeMiddlePart(domainName || '')

  return `${anonymizedLocal}@${anonymizedDomain}.${extension || ''}`
}

/**
 * Anonymizes a phone number by masking the middle digits
 * Example: +48123456789 -> +48***456789 or 123456789 -> 123***789
 */
export const anonymizePhoneNumber = (phone: string): string => {
  if (!phone || typeof phone !== 'string') return phone

  // Remove all non-digit characters for processing
  const digitsOnly = phone.replace(/\D/g, '')

  if (digitsOnly.length < 6) {
    // Too short to anonymize meaningfully
    return phone
  }

  // Keep original formatting structure
  let result = phone
  const totalDigits = digitsOnly.length

  // Calculate how many digits to keep at start and end
  const keepStart = Math.min(3, Math.floor(totalDigits * 0.3))
  const keepEnd = Math.min(3, Math.floor(totalDigits * 0.3))
  const maskLength = totalDigits - keepStart - keepEnd

  if (maskLength <= 0) return phone

  // Build the anonymized version
  const mask = '*'.repeat(Math.min(maskLength, 3)) // Limit mask to 3 characters for readability

  // Replace digits in original string while preserving formatting
  let digitIndex = 0
  let inMaskRegion = false

  result = phone.replace(/\d/g, (digit) => {
    if (digitIndex < keepStart) {
      digitIndex++
      return digit
    } else if (digitIndex >= totalDigits - keepEnd) {
      digitIndex++
      return digit
    } else {
      digitIndex++
      if (!inMaskRegion) {
        inMaskRegion = true
        return mask
      } else {
        // Skip this digit (already replaced by mask)
        return ''
      }
    }
  })

  // Clean up any multiple consecutive formatting characters
  result = result.replace(/[\s\-\(\)]{2,}/g, (match) => match[0])

  return result
}

/**
 * Helper function to anonymize the middle part of a string
 */
const anonymizeMiddlePart = (str: string): string => {
  if (!str || str.length <= 3) {
    return str
  }

  if (str.length <= 6) {
    // For short strings, show first and last character
    return `${str[0]}***${str[str.length - 1]}`
  }

  // For longer strings, show first 2 and last 2 characters
  const start = str.substring(0, 2)
  const end = str.substring(str.length - 2)
  return `${start}***${end}`
}

/**
 * Anonymizes a person's name by showing only first letter of first name and full last name
 * Example: Jan Kowalski -> J. Kowalski
 */
export const anonymizeName = (firstName: string, lastName: string): string => {
  if (!firstName && !lastName) return ''
  if (!firstName) return lastName
  if (!lastName) return `${firstName[0]}.`

  return `${firstName[0]}. ${lastName}`
}

/**
 * Checks if the current user has permission to view full client data
 * This would typically check user roles/permissions
 * For now, returns false to enable anonymization by default
 */
export const canViewFullClientData = (): boolean => {
  // TODO: Implement actual permission checking based on user roles
  // Example: return user?.role === 'admin' || user?.permissions?.includes('view_full_client_data')
  return false
}
